import { useState } from 'react'
import { useNavigate } from 'react-router'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Flashbar from '@cloudscape-design/components/flashbar'
import Header from '@cloudscape-design/components/header'
import Modal from '@cloudscape-design/components/modal'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import { useEmailCampaigns, useDeleteEmailCampaign } from '@/hooks/useEmailCampaigns'
import { useAccount } from '@/hooks/useAccount'
import type { EmailCampaign } from '@/lib/types'
import SenderEmailBadge from '@/components/common/SenderEmailBadge'
import CampaignWizard from './CampaignWizard'

interface OutreachCampaignsTabProps {
  clientId: string
}

type View =
  | { type: 'list' }
  | { type: 'wizard'; campaign?: EmailCampaign }

function campaignStatusIndicator(status: string) {
  switch (status) {
    case 'sent':
      return <StatusIndicator type="success">Sent</StatusIndicator>
    case 'sending':
      return <StatusIndicator type="loading">Sending…</StatusIndicator>
    case 'partial_failure':
      return <StatusIndicator type="warning">Partial Failure</StatusIndicator>
    case 'in_progress':
      return <StatusIndicator type="in-progress">In Progress</StatusIndicator>
    case 'draft':
      return <StatusIndicator type="pending">Draft</StatusIndicator>
    default:
      return <StatusIndicator type="info">{status}</StatusIndicator>
  }
}

function isResumable(status: string) {
  return status === 'draft' || status === 'in_progress'
}

interface DeleteCampaignConfirmModalProps {
  campaign: EmailCampaign
  onConfirm: () => void
  onDismiss: () => void
  loading: boolean
}

function DeleteCampaignConfirmModal({ campaign, onConfirm, onDismiss, loading }: DeleteCampaignConfirmModalProps) {
  return (
    <Modal
      visible={true}
      header="Delete Campaign"
      onDismiss={onDismiss}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={loading} onClick={onConfirm}>Delete</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <Box variant="p">Delete "<strong>{campaign.name}</strong>"?</Box>
        <Box variant="p" color="text-status-error">This action cannot be undone.</Box>
      </SpaceBetween>
    </Modal>
  )
}

export default function OutreachCampaignsTab({ clientId }: OutreachCampaignsTabProps) {
  const { campaigns, loading, error } = useEmailCampaigns(clientId)
  const { deleteCampaign, loading: deleting } = useDeleteEmailCampaign()
  const { account } = useAccount()
  const navigate = useNavigate()
  const [view, setView] = useState<View>({ type: 'list' })
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([])
  const [dismissedBannerIds, setDismissedBannerIds] = useState<Set<string>>(new Set())
  const [campaignToDelete, setCampaignToDelete] = useState<EmailCampaign | null>(null)

  if (error) {
    return (
      <Flashbar
        items={[{
          type: 'error',
          content: `Failed to load campaigns: ${error.message}`,
          dismissible: true,
          onDismiss: () => {},
        }]}
      />
    )
  }

  if (view.type === 'wizard') {
    return (
      <CampaignWizard
        clientId={clientId}
        initialCampaign={view.campaign}
        onDismiss={() => setView({ type: 'list' })}
        onComplete={(campaign) => {
          navigate(campaign.id)
        }}
      />
    )
  }

  const resumeBannerItems: FlashbarProps.MessageDefinition[] = campaigns
    .filter((c) => c.status === 'in_progress' && !dismissedBannerIds.has(c.id))
    .map((c) => ({
      type: 'info' as const,
      content: `"${c.name}" has an unfinished email draft.`,
      action: (
        <Button variant="inline-link" onClick={() => setView({ type: 'wizard', campaign: c })}>
          Resume
        </Button>
      ),
      dismissible: true,
      onDismiss: () => setDismissedBannerIds((prev) => new Set([...prev, c.id])),
    }))

  const allFlashItems = [...resumeBannerItems, ...flash]

  return (
    <SpaceBetween size="m">
      {campaignToDelete && (
        <DeleteCampaignConfirmModal
          campaign={campaignToDelete}
          loading={deleting}
          onDismiss={() => setCampaignToDelete(null)}
          onConfirm={async () => {
            try {
              await deleteCampaign(clientId, campaignToDelete.id)
              setCampaignToDelete(null)
            } catch (err) {
              setCampaignToDelete(null)
              setFlash([{
                type: 'error',
                content: `Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`,
                dismissible: true,
                onDismiss: () => setFlash([]),
              }])
            }
          }}
        />
      )}
      {allFlashItems.length > 0 && <Flashbar items={allFlashItems} />}
      <Table
        header={
          <Header
            counter={campaigns.length > 0 ? `(${campaigns.length})` : undefined}
            description={
              <SenderEmailBadge
                platforms={account?.platforms ?? []}
                defaultEmailProvider={account?.defaultEmailProvider}
              />
            }
            actions={
              <Button variant="primary" onClick={() => setView({ type: 'wizard' })}>
                New Campaign
              </Button>
            }
          >
            Email Campaigns
          </Header>
        }
        loading={loading}
        loadingText="Loading campaigns…"
        items={campaigns}
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (item) => item.name,
            sortingField: 'name',
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => campaignStatusIndicator(item.status),
          },
          {
            id: 'providers',
            header: 'Providers',
            cell: (item) => item.providers.length,
          },
          {
            id: 'sent',
            header: 'Sent',
            cell: (item) => item.sentCount,
          },
          {
            id: 'replied',
            header: 'Replied',
            cell: (item) => item.repliedCount,
          },
          {
            id: 'createdAt',
            header: 'Created',
            cell: (item) => new Date(item.createdAt).toLocaleDateString(),
            sortingField: 'createdAt',
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="inline-link"
                  onClick={() => {
                    if (isResumable(item.status)) {
                      setView({ type: 'wizard', campaign: item })
                    } else {
                      navigate(item.id)
                    }
                  }}
                >
                  {isResumable(item.status) ? 'Resume' : 'View'}
                </Button>
                <Button
                  variant="icon"
                  iconName="remove"
                  ariaLabel="Delete campaign"
                  onClick={() => setCampaignToDelete(item)}
                />
              </SpaceBetween>
            ),
          },
        ]}
        empty={
          <Box textAlign="center" padding="xxl">
            <SpaceBetween size="m" alignItems="center">
              <Box variant="p" color="text-status-inactive">No campaigns yet.</Box>
              <Button variant="primary" onClick={() => setView({ type: 'wizard' })}>
                New Campaign
              </Button>
            </SpaceBetween>
          </Box>
        }
      />
    </SpaceBetween>
  )
}
